import { useState, useCallback, useEffect } from 'react';

export type View = 'chat' | 'board';

const STORAGE_KEY = 'talkyn-active-view';
const VALID_VIEWS: View[] = ['chat', 'board'];

function getInitialView(): View {
  // Hash takes priority over localStorage
  const hash = window.location.hash.replace('#', '') as View;
  if (VALID_VIEWS.includes(hash)) return hash;

  const stored = localStorage.getItem(STORAGE_KEY) as View | null;
  if (stored && VALID_VIEWS.includes(stored)) return stored;

  return 'chat';
}

export function useViewRouter() {
  const [activeView, setActiveView] = useState<View>(getInitialView);

  // Sync hash on mount
  useEffect(() => {
    window.location.hash = activeView;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '') as View;
      if (VALID_VIEWS.includes(hash)) {
        setActiveView(hash);
        localStorage.setItem(STORAGE_KEY, hash);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const switchView = useCallback((view: View) => {
    setActiveView(view);
    localStorage.setItem(STORAGE_KEY, view);
    window.location.hash = view;
  }, []);

  return { activeView, switchView };
}
